import React, { useState, useMemo } from 'react'

import {
  Button,
  SearchInput,
  FormField,
  FormItem,
  FormControl,
  FormMessage,
  FormDescription,
  FormLabel,
  FloatingLabelInput,
  LocationAutoComplete,
  PlaceResult,
  AutosizeTextarea,
  Input,
  Badge,
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
  Checkbox,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  ScrollArea,
  Slider,
  PriceRangeSlider,
} from 'src/components/ui'
import { mapItem } from 'src/schemas/utils'
import { cn } from 'src/utils/cn'
import { formatPhoneNumber } from 'src/utils/format-phone-number'

type StandardFormFieldProps = {
  control: any
  name: string
  label: string
  fieldType:
    | 'input'
    | 'select'
    | 'textarea'
    | 'button-select'
    | 'button-multi-select'
    | 'multi-select'
    | 'search-multi-select'
    | 'month-year-select'
    | 'date-select'
    | 'location'
    | 'checkbox'
    | 'code'
    | 'slider'
    | 'price-range'
    | 'phone'
  inputType?: 'email' | 'password' | 'text' | 'phone' | 'number'
  options?: Record<string, mapItem>
  handlePlaceSelected?: (place: PlaceResult) => void
  locationTypes?: string[]
  locationRefValue?: 'street' | 'city' | ''
  description?: string
  className?: string
  floatingLabel?: boolean
  onValueChange?: (value: any) => void
  min?: number
  max?: number
  step?: number
  disabled?: boolean
  fieldClassName?: string
}

export const StandardFormField: React.FC<StandardFormFieldProps> = ({
  control,
  name,
  label,
  fieldType,
  inputType,
  options,
  handlePlaceSelected,
  locationTypes,
  locationRefValue,
  description,
  className,
  floatingLabel = ['input', 'phone', 'textarea', 'location'].includes(fieldType),
  onValueChange,
  min,
  max,
  step,
  disabled,
  fieldClassName,
}) => {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          {!floatingLabel && <FormLabel>{label}</FormLabel>}
          <FormControl>
            <FormControlContents
              fieldType={fieldType}
              field={field}
              label={label}
              inputType={inputType}
              options={options}
              handlePlaceSelected={handlePlaceSelected}
              locationTypes={locationTypes}
              locationRefValue={locationRefValue}
              floatingLabel={floatingLabel}
              onValueChange={onValueChange}
              min={min}
              max={max}
              step={step}
              disabled={disabled}
              fieldClassName={fieldClassName}
            />
          </FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  )
}

const FormControlContents: React.FC<{
  fieldType: string
  field: any
  label: string
  inputType?: 'email' | 'password' | 'text' | 'phone' | 'number'
  options?: Record<string, mapItem>
  handlePlaceSelected?: (place: PlaceResult) => void
  locationTypes?: string[]
  locationRefValue?: 'street' | 'city' | ''
  floatingLabel?: boolean
  onValueChange?: (value: any) => void
  min?: number
  max?: number
  step?: number
  disabled?: boolean
  fieldClassName?: string
}> = ({
  fieldType,
  field,
  label,
  inputType,
  options,
  handlePlaceSelected,
  locationTypes,
  locationRefValue,
  floatingLabel,
  onValueChange,
  min,
  max,
  step,
  disabled,
  fieldClassName,
}) => {
  const [searchTerm, setSearchTerm] = useState('')

  const filteredOptions = useMemo(() => {
    if (!options) return []

    const selectedValues = field.value || []

    let filtered = Object.entries(options)

    if (searchTerm) {
      filtered = filtered.filter(([_, { label }]) => label.toLowerCase().startsWith(searchTerm.toLowerCase()))
    } else {
      const selectedOptions = filtered.filter(([key]) => selectedValues.includes(key))
      const unselectedOptions = filtered.filter(([key]) => !selectedValues.includes(key)).slice(0, 0)
      filtered = [...selectedOptions, ...unselectedOptions]
    }

    return filtered.sort(([keyA], [keyB]) => {
      const aSelected = selectedValues.includes(keyA)
      const bSelected = selectedValues.includes(keyB)
      if (aSelected && !bSelected) return -1
      if (!aSelected && bSelected) return 1
      return 0
    })
  }, [searchTerm, field.value, options])

  switch (fieldType) {
    case 'input':
      if (inputType === 'number') {
        return floatingLabel ? (
          <FloatingLabelInput
            {...field}
            label={label}
            id={field.name}
            type={inputType}
            disabled={disabled}
            className={fieldClassName}
            onChange={(e) => {
              const value = e.target.value ? Number(e.target.value) : undefined
              field.onChange(value)
            }}
          />
        ) : (
          <Input
            {...field}
            label={label}
            id={field.name}
            type={inputType}
            disabled={disabled}
            className={fieldClassName}
            onChange={(e) => {
              const value = e.target.value ? Number(e.target.value) : undefined
              field.onChange(value)
            }}
          />
        )
      }
      return floatingLabel ? (
        <FloatingLabelInput {...field} label={label} id={field.name} type={inputType} disabled={disabled} />
      ) : (
        <Input {...field} label={label} id={field.name} type={inputType} disabled={disabled} />
      )
    case 'phone':
      return floatingLabel ? (
        <FloatingLabelInput
          {...field}
          label={label}
          id={field.name}
          type="tel"
          disabled={disabled}
          onChange={(e) => {
            const formatted = formatPhoneNumber(e.target.value)
            field.onChange(formatted)
          }}
          value={field.value || ''}
          className={fieldClassName}
        />
      ) : (
        <Input
          {...field}
          label={label}
          id={field.name}
          type="tel"
          disabled={disabled}
          onChange={(e) => {
            const formatted = formatPhoneNumber(e.target.value)
            field.onChange(formatted)
          }}
          value={field.value || ''}
          className={fieldClassName}
        />
      )
    case 'select':
      if (!options) return null
      return (
        <div className={cn('relative', fieldClassName)}>
          <Select
            onValueChange={(value) => {
              field.onChange(value)
              onValueChange?.(value)
            }}
            value={field.value}
            disabled={disabled}
          >
            <SelectTrigger>
              <SelectValue placeholder={`${label.charAt(0).toUpperCase() + label.slice(1)}`} />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(options).map(([value, { label }]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {field.value && (
            <label
              htmlFor={field.name}
              className={cn(
                'absolute left-3 top-2 z-10 origin-[0] -translate-y-4 scale-75 transform cursor-text bg-background px-2 text-sm text-muted duration-300',
                'peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:scale-100',
              )}
            >
              {label}
            </label>
          )}
        </div>
      )
    case 'textarea':
      return (
        <AutosizeTextarea
          {...field}
          label={label}
          maxHeight={5}
          value={field.value || ''}
          onChange={(e) => field.onChange(e.target.value)}
          disabled={disabled}
          className={fieldClassName}
        />
      )
    case 'location':
      if (!handlePlaceSelected) return null
      return (
        <LocationAutoComplete
          onPlaceSelected={handlePlaceSelected}
          placeholder={`${label}`}
          componentRestrictions={{ country: 'us' }}
          types={locationTypes || ['address']}
          refValue={locationRefValue || 'street'}
          inputAutocompleteValue={locationRefValue === 'street' ? 'street_address' : ''}
          value={field.value}
        />
      )
    case 'multi-select':
      if (!options) return null
      return (
        <div className={cn('flex flex-wrap gap-2', fieldClassName)}>
          {Object.entries(options).map(([value, { label }]) => {
            const isSelected = field.value?.includes(value)
            return (
              <Badge
                key={value}
                variant={isSelected ? 'secondary' : 'outline'}
                text={label}
                showCloseIcon={isSelected}
                onClick={() => {
                  const updatedValues = isSelected
                    ? field.value?.filter((val: string) => val !== value)
                    : [...(field.value || []), value]
                  field.onChange(updatedValues)
                }}
              >
                <span>{label}</span>
              </Badge>
            )
          })}
        </div>
      )
    case 'button-multi-select':
      if (!options) return null
      return (
        <div className={cn('grid grid-cols-2 gap-2', fieldClassName)}>
          {Object.entries(options).map(([value, { label }]) => {
            const isSelected = field.value?.includes(value)
            return (
              <Button
                key={value}
                type="button"
                variant={isSelected ? 'fadedSecondary' : 'accent'}
                onClick={() => {
                  const updatedValues = isSelected
                    ? field.value?.filter((val: string) => val !== value)
                    : [...(field.value || []), value]
                  field.onChange(updatedValues)
                }}
                showCloseIcon={isSelected}
                className="h-16 w-full"
              >
                <span className="whitespace-normal text-center font-medium">{label}</span>
              </Button>
            )
          })}
        </div>
      )
    case 'button-select':
      if (!options) return null
      return (
        <div className={cn('grid grid-cols-2 gap-4', fieldClassName)}>
          {Object.entries(options).map(([value, { label, icon: Icon }]) => {
            const isSelected = field.value === value
            return (
              <Button
                key={value}
                type="button"
                variant={isSelected ? 'fadedSecondary' : 'accent'}
                aria-selected={isSelected}
                onClick={() => field.onChange(isSelected ? null : value)}
                showCloseIcon={isSelected}
                className="flex h-16 flex-col items-center justify-center"
              >
                <div className="flex flex-col items-center justify-center gap-2">
                  {Icon && <Icon className="size-12 shrink-0" />}
                  <span className="line-clamp-2 text-center font-medium">{label}</span>
                </div>
              </Button>
            )
          })}
        </div>
      )
    case 'search-multi-select':
      return (
        <div className={fieldClassName}>
          <SearchInput onChange={setSearchTerm} placeholder={`Search...`} delay={300}>
            <div className="mt-2 flex flex-wrap gap-2">
              {filteredOptions.map(([value, { label }]) => {
                const isSelected = field.value?.includes(value)
                return (
                  <Badge
                    key={value}
                    variant={isSelected ? 'secondary' : 'outline'}
                    text={label}
                    showCloseIcon={isSelected}
                    onClick={() => {
                      const updatedValues = isSelected
                        ? field.value?.filter((val: string) => val !== value)
                        : [...(field.value || []), value]
                      field.onChange(updatedValues)
                    }}
                  >
                    <span>{label}</span>
                  </Badge>
                )
              })}
            </div>
          </SearchInput>
        </div>
      )
    case 'code':
      return (
        <div className={cn('flex items-center justify-center', fieldClassName)}>
          <InputOTP maxLength={6} {...field}>
            <InputOTPGroup>
              <InputOTPSlot index={0} />
              <InputOTPSlot index={1} />
              <InputOTPSlot index={2} />
              <InputOTPSlot index={3} />
              <InputOTPSlot index={4} />
              <InputOTPSlot index={5} />
            </InputOTPGroup>
          </InputOTP>
        </div>
      )
    case 'checkbox':
      if (!options) return null
      return (
        <div className={cn('flex items-center justify-center', fieldClassName)}>
          <ScrollArea className="h-64 flex-1">
            {Object.entries(options).map(([value, { label }]) => (
              <div key={value} className="mb-2 flex items-center space-x-3 space-y-0 rounded-md border p-4">
                <Checkbox
                  id={`${name}-${value}`}
                  checked={field.value?.includes(value)}
                  onCheckedChange={(checked) => {
                    const updatedOptions = checked
                      ? [...(field.value || []), value]
                      : (field.value || []).filter((val: string) => val !== value)
                    field.onChange(updatedOptions)
                  }}
                />
                <FormLabel htmlFor={`${name}-${value}`} className="m-0 flex-1 cursor-pointer">
                  {label}
                </FormLabel>
              </div>
            ))}
          </ScrollArea>
        </div>
      )
    case 'month-year-select':
      return (
        <div className={fieldClassName}>
          <DateSelect value={field.value ? new Date(field.value) : null} onChange={(date) => field.onChange(date)} />{' '}
        </div>
      )
    case 'date-select':
      return (
        <div className={fieldClassName}>
          <DateSelect
            value={field.value ? new Date(field.value) : null}
            onChange={(date) => field.onChange(date)}
            includeDay
          />{' '}
        </div>
      )
    case 'slider':
      return (
        <div className={fieldClassName}>
          <Slider
            value={[field.value]}
            onValueChange={(value) => field.onChange(value[0])}
            min={0}
            max={100}
            step={1}
            showCircle
            showValue
          />
        </div>
      )
    case 'price-range': {
      const [minFieldName, maxFieldName] = field.name.split(',')
      const minField = field.value?.[0] || min || 0
      const maxField = field.value?.[1] || max || 1000000
      return (
        <div className={fieldClassName}>
          <PriceRangeSlider
            field={{
              value: [minField, maxField],
              onChange: ([newMin, newMax]) => {
                // @ts-ignore: TODO: fix this name is deprecated
                field.onChange({
                  target: { name: minFieldName, value: newMin },
                })
                field.onChange({
                  target: { name: maxFieldName, value: newMax },
                })
              },
            }}
            min={min || 0}
            max={max || 1000000}
            step={step || 10000}
          />
        </div>
      )
    }
    default:
      return null
  }
}

interface DateSelectProps {
  value: Date | null
  onChange: (value: string) => void
  includeDay?: boolean
}

const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

const years = Array.from({ length: 100 }, (_, i) => (new Date().getFullYear() - i).toString())
const days = Array.from({ length: 31 }, (_, i) => (i + 1).toString())

const DateSelect: React.FC<DateSelectProps> = ({ value, onChange, includeDay = false }) => {
  const currentMonth = value ? months[value.getMonth()] : ''
  const currentYear = value ? value.getFullYear().toString() : ''
  const currentDay = value ? value.getDate().toString() : includeDay ? '' : '1'

  const handleMonthChange = (month: string) => {
    const monthIndex = months.indexOf(month)
    const newDate = new Date(value || new Date())
    newDate.setMonth(monthIndex)
    newDate.setDate(includeDay ? (currentDay === '' ? 1 : parseInt(currentDay, 10)) : 1)
    onChange(newDate.toISOString().split('T')[0])
  }

  const handleYearChange = (year: string) => {
    const newDate = new Date(value || new Date())
    newDate.setFullYear(parseInt(year, 10))
    newDate.setDate(includeDay ? (currentDay === '' ? 1 : parseInt(currentDay, 10)) : 1)
    onChange(newDate.toISOString().split('T')[0])
  }

  const handleDayChange = (day: string) => {
    const newDate = new Date(value || new Date())
    newDate.setDate(parseInt(day, 10))
    onChange(newDate.toISOString().split('T')[0])
  }

  return (
    <div className="flex space-x-2">
      <Select onValueChange={handleMonthChange} value={currentMonth}>
        <SelectTrigger>
          <SelectValue placeholder="Month" />
        </SelectTrigger>
        <SelectContent>
          {months.map((month) => (
            <SelectItem key={month} value={month}>
              {month}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {includeDay && (
        <Select onValueChange={handleDayChange} value={currentDay}>
          <SelectTrigger>
            <SelectValue placeholder="Day" />
          </SelectTrigger>
          <SelectContent>
            {days.map((day) => (
              <SelectItem key={day} value={day}>
                {day}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      <Select onValueChange={handleYearChange} value={currentYear}>
        <SelectTrigger>
          <SelectValue placeholder="Year" />
        </SelectTrigger>
        <SelectContent>
          {years.map((year) => (
            <SelectItem key={year} value={year}>
              {year}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

export default DateSelect
